export class DateUtils {

  constructor() {}

  // =========================
  // Utility to parse any date string safely (iOS-compatible)
  // =========================
  public static parseDateString(date: string | Date | number): Date {
    if (!date) return new Date(NaN);

    if (date instanceof Date) return date;
    if (typeof date === 'number') return new Date(date);

    // Convert "yyyy-mm-dd hh:mm:ss" → "yyyy-mm-ddTHH:mm:ss" for ISO
    date = date.replace(' ', 'T');

    const parts = date.split('T')[0].split('-').map(Number);
    if (parts.length === 3) {
      // new Date(year, monthIndex, day)
      return new Date(parts[0], parts[1] - 1, parts[2]);
    }

    const parsed = new Date(date);
    return isNaN(parsed.getTime()) ? new Date(NaN) : parsed;
  }

  // =========================
  // Get current date with optional offset (returns object)
  // =========================
  public getNow(dayOffset: number = 0): { year: number; month: number; day: number } {
    const date = new Date();
    date.setDate(date.getDate() + dayOffset);
    return { year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() };
  }

  // =========================
  // Get date string with offset days
  // =========================
  public getDateWithOffset(dayOffset: number = 0): string {
    const date = new Date();
    date.setDate(date.getDate() + dayOffset);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
  }

  // =========================
  // Get first day of month considering offset
  // =========================
  public getFirstDayOfMonth(dayOffset: number = 0): { year: number; month: number; day: number } {
    const now = this.getNow(dayOffset);
    return { year: now.year, month: now.month, day: 1 };
  }

  // =========================
  // Parse date string according to format
  // Example: parse("2025-08-25", "yyyy-mm-dd")
  // =========================
  public parse(input: string, format: string = 'yyyy-mm-dd'): Date {
    const parts = input.match(/(\d+)/g);
    if (!parts) return new Date(NaN);

    const fmt: { [key: string]: number } = {};
    let i = 0;
    format.replace(/(yyyy|yy|mm|dd)/g, (part) => { fmt[part] = i++; return ''; });

    const year = parseInt(parts[fmt['yyyy']] || parts[fmt['yy']], 10);
    const month = parseInt(parts[fmt['mm']], 10) - 1;
    const day = parseInt(parts[fmt['dd']], 10);

    return new Date(year, month, day);
  }

  // =========================
  // Static helper: get date object from string
  // =========================
  public static getDateObjByStr(date: string): { year: number; month: number; day: number } {
    const dt = date.split('-').map(Number);
    return { year: dt[0] || 0, month: dt[1] || 0, day: dt[2] || 0 };
  }

  // =========================
  // Static helper: get string from date object
  // =========================
  public static getDateStrByObj(dtObj: { year: number; month: number; day: number }): string {
    return `${dtObj.year}-${String(dtObj.month).padStart(2,'0')}-${String(dtObj.day).padStart(2,'0')}`;
  }

  // =========================
  // Static helper: get month name
  // =========================
  public static getMonthName(monthNumber: number | string) {
    monthNumber = +monthNumber;
    const date = new Date();
    date.setMonth(monthNumber - 1);
    return date.toLocaleString('default', { month: 'long' });
  }

  // =========================
  // Format date safely for all devices
  // =========================
  public static format(date: string | Date | number, formatString: string = 'yy-MM-dd'): string {
    const d = DateUtils.parseDateString(date);
    if (isNaN(d.getTime())) return '';

    const pad = (n: number, len: number = 2) => String(n).padStart(len,'0');
    const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const hour12 = d.getHours() % 12 || 12;
    const ampm = d.getHours() >= 12 ? 'PM' : 'AM';

    const replacements: { [key: string]: number | string } = {
      YYYY: d.getFullYear(),
      yyyy: d.getFullYear(),
      yy: String(d.getFullYear()).slice(-2),
      MM: pad(d.getMonth() + 1),
      MMM: monthNames[d.getMonth()],
      DD: pad(d.getDate()),
      dd: pad(d.getDate()),
      HH: pad(d.getHours()),
      hh: pad(hour12),
      h: String(hour12),
      mm: pad(d.getMinutes()),
      ss: pad(d.getSeconds()),
      SSS: pad(d.getMilliseconds(), 3),
      A: ampm,
      a: ampm.toLowerCase(),
    };

    return formatString.replace(
      /YYYY|yyyy|yy|MM|MMM|DD|dd|HH|hh|h|mm|ss|SSS|A|a/g,
      match => String(replacements[match])
    );
  }

}
